import React from "react";
import * as Sentry from "@sentry/react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";
import type { User } from "firebase/auth";
import { APP_VERSION, GIT_SHA, BUILD_TIME } from "./version";

// Environment variables from Vite
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || "development";
const TRACES_SAMPLE_RATE = parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || "1.0");
const REPLAYS_SESSION_SAMPLE_RATE = parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || "0.1");
const REPLAYS_ON_ERROR_SAMPLE_RATE = parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || "1.0");
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL as string | undefined;

export type TracePropagationFields = {
  sentryTrace?: string;
  sentryBaggage?: string;
};

/** Capture current Sentry trace headers for Storage customMetadata / outbound HTTP. */
export const getTracePropagationFields = (): TracePropagationFields => {
  try {
    const traceData = Sentry.getTraceData();
    return {
      sentryTrace: traceData["sentry-trace"] || undefined,
      sentryBaggage: traceData.baggage || undefined,
    };
  } catch {
    return {};
  }
};

// Helper function to set user context
export const setSentryUser = (user: User | null, additionalInfo?: {
  accountId?: string | null;
  isSuperAdmin?: boolean;
  isAccountAdmin?: boolean;
}) => {
  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.uid,
    email: user.email || undefined,
    displayName: user.displayName || undefined,
    // Additional context for better debugging
    accountId: additionalInfo?.accountId || undefined,
    isSuperAdmin: additionalInfo?.isSuperAdmin || false,
    isAccountAdmin: additionalInfo?.isAccountAdmin || false,
  });
  // Sentry only indexes standard user fields (id/email/username) for search
  // by default — accountId above is visible on the event but isn't reliably
  // queryable. A real tag is what the customer-usage digest's Sentry query
  // client (sentryUsage.services.ts) actually searches by (office_id:X).
  if (additionalInfo?.accountId) {
    Sentry.setTag("office_id", additionalInfo.accountId);
  }
};

// Helper function to capture Firebase Function errors with context
export const captureFirebaseFunctionError = (error: Error, functionName: string, context?: Record<string, unknown>) => {
  Sentry.withScope((scope) => {
    scope.setTag("function", functionName);
    scope.setTag("errorType", "firebase_function");
    if (context) {
      scope.setContext("function_context", context);
    }
    Sentry.captureException(error);
  });
};

// Initialize Sentry
export const initializeSentry = () => {
  // Don't initialize in development without DSN
  if (import.meta.env.DEV && !SENTRY_DSN) {
    console.log("🔧 Sentry disabled in development (no DSN provided)");
    return;
  }

  // Don't initialize if no DSN
  if (!SENTRY_DSN || SENTRY_DSN === "your_sentry_dsn_here") {
    console.log("🔧 Sentry disabled (no DSN provided)");
    return;
  }

  // Do NOT include firebasestorage.googleapis.com — GCS CORS rejects
  // sentry-trace / baggage on XHR uploads (preflight Access-Control-Allow-Headers).
  // Storage tracing uses customMetadata via getTracePropagationFields() instead.
  const tracePropagationTargets: (string | RegExp)[] = ["localhost"];
  if (BACKEND_URL) {
    try {
      const origin = new URL(BACKEND_URL).origin;
      tracePropagationTargets.push(origin);
    } catch {
      // ignore invalid URL
    }
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,

    // Performance monitoring
    tracesSampleRate: TRACES_SAMPLE_RATE,

    // Propagate sentry-trace / baggage to Nest only (not Firebase Storage)
    tracePropagationTargets,

    // Session replay
    replaysSessionSampleRate: REPLAYS_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate: REPLAYS_ON_ERROR_SAMPLE_RATE,

    // Integrations
    integrations: [
      // React Router v7 integration
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),

      // Session replay with privacy settings
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),

      // Browser profiling for performance
      Sentry.browserProfilingIntegration(),

      // Capture console errors
      Sentry.captureConsoleIntegration({
        levels: ["error"],
      }),
    ],

    // Set release version (will be updated after version.ts is created)
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,

    // Ignore specific errors that are expected
    beforeSend(event, hint) {
      // Ignore Firebase auth errors that are expected
      if (hint?.originalException instanceof Error) {
        const errorMessage = hint.originalException.message;

        // Expected Firebase auth errors
        if (errorMessage.includes("auth/user-not-found") ||
            errorMessage.includes("auth/wrong-password") ||
            errorMessage.includes("auth/email-already-in-use") ||
            errorMessage.includes("auth/weak-password") ||
            errorMessage.includes("auth/invalid-email") ||
            errorMessage.includes("auth/too-many-requests")) {
          console.log("🔧 Ignoring expected Firebase auth error:", errorMessage);
          return null;
        }

        // Network errors that might not be actionable
        if (errorMessage.includes("Network Error") ||
            errorMessage.includes("Failed to fetch") ||
            errorMessage.includes("Load failed")) {
          // Don't completely ignore, but add context
          event.tags = { ...event.tags, errorType: "network" };
        }
      }

      return event;
    },

    // Add context about the app
    initialScope: {
      tags: {
        framework: "React",
        router: "React Router v7",
        stateManagement: "Redux Toolkit",
        uiLibrary: "Shadcn UI",
        version: APP_VERSION,
        gitSha: GIT_SHA,
      },
      contexts: {
        app: {
          version: APP_VERSION,
          gitSha: GIT_SHA,
          buildTime: BUILD_TIME,
          environment: SENTRY_ENVIRONMENT,
        },
      },
    },
  });

  // Log initialization in development
  if (import.meta.env.DEV) {
    console.log("🚀 Sentry initialized", {
      dsn: SENTRY_DSN ? "configured" : "missing",
      environment: SENTRY_ENVIRONMENT,
      tracesSampleRate: TRACES_SAMPLE_RATE,
      version: APP_VERSION,
      gitSha: GIT_SHA,
    });
  }
};

// Export Sentry for manual usage in components
export { Sentry };
